import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { useCreateTeamMeetingPost } from '../../../hooks/useTeamMeetings.js';
import { STICKY_CLASS, STICKY_SIZE, stickyStyle } from './StickyNote.js';

/**
 * 区分の置き場の最後に置く「付箋を貼る」。押すと書く人の色の付箋が開き、書いて貼る
 * （Ctrl / ⌘ + Enter でも貼れる）。開催回は指定しない（いつ書いても次の MTG で棚卸しされる）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG のボードの区分に、書く人の色の付箋を開いて本文を書きいつでも投稿し、弾かれたら API の文言をそのまま出す
 * @graph-connects useCreateTeamMeetingPost [calls] 投稿
 * @graph-connects stickyStyle [calls] 書く人の色
 */
export function PostComposer({
  seriesId,
  category,
  viewerId,
  className = '',
}: {
  seriesId: string;
  category: string;
  /** 書く人（付箋の色に使う）。分からなければ既定の色 */
  viewerId: string | null;
  className?: string;
}): ReactNode {
  const { t } = useTranslation();
  const create = useCreateTeamMeetingPost(seriesId);
  const [isOpen, setIsOpen] = useState(false);
  const [body, setBody] = useState('');

  if (!isOpen) {
    return (
      <button
        type="button"
        className={`${STICKY_SIZE} flex items-center justify-center rounded-[3px] border-2 border-dashed border-[#b4c6de] text-sm text-[#7d93b2] transition-colors hover:bg-white/60 ${className}`}
        onClick={() => {
          setIsOpen(true);
        }}
      >
        <span aria-hidden="true" className="mr-1 text-lg leading-none">
          ＋
        </span>
        {t('teamMeetings.board.addSticky')}
      </button>
    );
  }

  const submit = () => {
    create.mutate(
      { category, body },
      {
        onSuccess: () => {
          setBody('');
          setIsOpen(false);
        },
      },
    );
  };
  const cancel = () => {
    setBody('');
    setIsOpen(false);
  };

  return (
    <form
      className={`${STICKY_CLASS} ${STICKY_SIZE} gap-2 ${className}`}
      style={stickyStyle(viewerId ?? '')}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <textarea
        // 押して開いた付箋なので、すぐ書けるようにする
        autoFocus
        rows={5}
        className="w-full flex-1 resize-none border-0 bg-transparent text-[13px] leading-snug shadow-none outline-none placeholder:text-[#1e1e1e]/40 focus:shadow-none focus:outline-none focus:ring-0"
        aria-label={t('teamMeetings.post.new')}
        placeholder={t('teamMeetings.post.newPlaceholder')}
        value={body}
        onChange={(e) => {
          setBody(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && body.trim() !== '') submit();
          if (e.key === 'Escape') cancel();
        }}
      />
      {create.isError && (
        <p className="whitespace-pre-wrap text-[11px] text-[#b42318]" role="alert">
          {create.error.message}
        </p>
      )}
      <div className="flex items-center justify-end gap-1">
        <Button type="button" size="sm" variant="ghost" onClick={cancel}>
          {t('teamMeetings.common.cancel')}
        </Button>
        <Button type="submit" size="sm" loading={create.isPending} disabled={body.trim() === ''}>
          {t('teamMeetings.post.submit')}
        </Button>
      </div>
    </form>
  );
}
